interface ProcessingPanelProps {
  message: string;
  processingTime: number;
  modelIcon: string;
  modelName: string;
  messageKey: string | number;
  className?: string;
  isStreaming?: boolean;  // NEW: Indicates streaming mode
  streamingProgress?: number;  // NEW: Progress percentage (0-100)
  chunkCount?: number;  // NEW: Number of chunks received
}

export default function ProcessingPanel({
  message,
  processingTime,
  modelIcon,
  modelName,
  messageKey,
  className = '',
  isStreaming = false,
  streamingProgress = 0,
  chunkCount = 0
}: ProcessingPanelProps) {
  return (
    <div className={`relative bg-gray-200 dark:bg-gray-950 border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 shadow-xl overflow-hidden hover:bg-gray-300 dark:hover:bg-gray-900 transition-colors ${className}`}>
      {/* Animated gradient backgrounds - multiple layers */}
      
      {/* Cyan gradient - fast */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(73, 243, 255, 0.51) 0%, transparent 60%)',
          backgroundSize: '200% 200%',
          mixBlendMode: 'plus-lighter',
          animation: 'slideGradient 12s linear infinite'
        }}
      />
      
      {/* Purple gradient - slow */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(154, 47, 255, 0.53) 0%, transparent 70%)',
          backgroundSize: '200% 200%',
          mixBlendMode: 'plus-lighter',
          animation: 'slideGradient 19s ease-in-out infinite'
        }}
      />
      
      {/* Green gradient - medium */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 241, 45, 0.69) 0%, transparent 50%)',
          backgroundSize: '150% 150%',
          mixBlendMode: 'overlay',
          animation: 'slideGradient 8s linear infinite'
        }}
      />
      
      {/* Pink gradient - very slow */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(236, 72, 154, 0.49) 0%, transparent 80%)',
          backgroundSize: '250% 250%',
          mixBlendMode: 'plus-lighter',
          animation: 'slideGradient 12s linear infinite'
        }}
      />
      
      {/* Blue gradient - reverse direction */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.09) 0%, transparent 65%)',
          backgroundSize: '180% 180%',
          mixBlendMode: 'plus-lighter',
          animation: 'slideGradient 7.5s linear infinite reverse'
        }}
      />
      
      {/* Solid color background layer - sits above gradients */}
      <div 
        className="absolute inset-1 bg-white/90 dark:bg-gray-900/80 z-[5] rounded-md"
      />
      
      <div className="relative z-10 flex items-center gap-2">
        <img 
          src={modelIcon} 
          alt={modelName} 
          className="w-5 h-5 object-contain rounded-full flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p 
            key={messageKey} 
            className="text-sm text-gray-700 dark:text-dark-text opacity-80 animate-drift-right"
          >
            {message}
          </p>
          {isStreaming && streamingProgress > 0 && (
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 h-1 bg-gray-300 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-300"
                  style={{ width: `${streamingProgress}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-dark-text-muted font-mono">
                {streamingProgress}%
              </span>
            </div>
          )}
        </div>
        <div className="flex-shrink-0">
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-xs text-gray-500 dark:text-dark-text-muted font-mono opacity-80">
              {processingTime.toFixed(1)}s
            </span>
            {isStreaming && chunkCount > 0 && (
              <span className="text-xs text-blue-600 dark:text-blue-400 font-mono opacity-70">
                {chunkCount} chunks
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

